package gr.tech.site.pages.states

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Width
import com.varabyte.kobweb.compose.css.objectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.components.states.FaqAccordion
import gr.tech.site.components.states.FaqItem
import gr.tech.site.components.states.RebateTypeGrid
import gr.tech.site.components.states.UtilityCardData
import gr.tech.site.components.states.UtilityPartnerGrid
import gr.tech.site.data.RebateTypes
import gr.tech.site.data.getStateInfo
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.styles.states.StateStatIconStyle
import gr.tech.site.styles.states.StateStatLabelStyle
import gr.tech.site.styles.states.StateStatNumberStyle
import gr.tech.site.styles.states.WashingtonHeroTextStyle
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initWashingtonPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Washington - LED Grow Light & HVAC Energy Rebates for Growers",
        description = "Washington utilities offer energy incentives on LED grow lights and HVAC upgrades for commercial growers. Green Rebates navigates the programs so you don't have to.",
        canonicalUrl = "https://greenrebates.com/states/washington",
        ogImage = "/images/washington1.jpg"
    ))
}

@Page()
@Layout(".components.layouts.PageLayout")
@Composable
fun WashingtonPage() {

    val stateInfo = getStateInfo("Washington");

    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // Section 1: HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        )
        {
            Img(
                src = ImageRepo.Washington1.src,
                alt = ImageRepo.Washington1.alt,
                attrs = Modifier.fillMaxSize().toAttrs {
                    attr("fetchpriority", "high")
                    style { objectFit(ObjectFit.Cover) }
                }
            )
            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .backgroundColor(Colors.Black)
                    .opacity(60.percent)
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .toAttrs()
            )
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                H3(
                    attrs = LandingHeroEyebrowStyle.toModifier()
                        .then(WashingtonHeroTextStyle.toModifier())
                        .classNames("text-uppercase", "mb-2").toAttrs()
                ) { Text("Washington Horticulture Energy Rebates") }

                H1(
                    attrs = LandingHeroHeadlineStyle.toModifier()
                        .then(WashingtonHeroTextStyle.toModifier())
                        .classNames("mb-3").toAttrs()
                ) { Text("Washington Growers: Claim Your LED Grow Light & HVAC Rebates") }

                P(
                    attrs = LandingHeroLeadStyle.toModifier()
                        .then(WashingtonHeroTextStyle.toModifier())
                        .opacity(0.87)
                        .classNames("mb-4", "fw-medium", "text-white").toAttrs()
                ) {
                    Text("Green Rebates works with Washington utilities to help cultivators claim up to 100% funding on DLC-qualified LED grow lights, HVAC, and dehumidification upgrades — from first call to incentive check.")
                }

                Link(
                    path = "/contact-us?state=Washington",
                    text = "Free Rebates Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaSecondaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free Washington grow light and HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // Section 2: UTILITIES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "pt-5", "pb-3")
                .id("utilities")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "pb-1", "mb-3").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) { Text("Common Washington Utilities") }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) {
                    Text("Green Rebates works directly with Washington utilities to secure LED grow light rebates, commercial HVAC incentives, and horticulture energy savings programs for growers across the state. " +
                            "We handle the details so your indoor grow or greenhouse captures every available rebate.")
                }

                UtilityPartnerGrid(
                    stateInfo.utilities.map { name ->
                        UtilityCardData(
                            utilityName = name,
                            state = stateInfo.name,
                            utilityParam = name,
                            favorite = name in stateInfo.favoriteUtilities
                        )
                    }
                )
            }


            Div(attrs = Modifier.classNames("container", "pb-1", "mt-4", "mb-2", "d-flex", "flex-column", "justify-content-center", "align-items-center").toAttrs()) {

                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-1").toAttrs()
                ) {
                    Text("Don't see your utility?")
                    SpanText(
                        text = " No problem!",
                        modifier = Modifier.fontWeight(FontWeight.Bold)
                    )
                }

                Link(
                    path = "/contact-us?state=Washington",
                    text = "Contact us \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaSecondaryStyle.toModifier()
                        .classNames("mt-2")
                        .role("button")
                        .attrsModifier { attr("aria-label", "Find out if your Washington grow facility qualifies for rebates") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }

        }

        // Section 3: Rebates Available
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) { Text("Available Washington Rebates") }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) {
                    Text("Washington utility energy efficiency programs are built for the state's grow operations.")
                }
                RebateTypeGrid(
                    rebateTypes = listOf(
                        RebateTypes.Lighting,
                        RebateTypes.Hvac,
                        RebateTypes.Dehu,
                        RebateTypes.Greenhouse,
                        RebateTypes.Other,
                        RebateTypes.EvChargers,
                        RebateTypes.Irrigation,
                        RebateTypes.Processing,
                        RebateTypes.ShadeInsulation,
                    ),
                    stateName = stateInfo.name
                )
            }
        }

        // Section 4: Do Rebates Apply to me?
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {

                Div(attrs = Modifier.classNames("row", "align-items-start", "g-4").toAttrs()) {

                    Div(attrs = Modifier.classNames("col-lg-6", "order-lg-1", "order-2").toAttrs()) {
                        Img(
                            src = ImageRepo.LEDGrowLights2.src,
                            alt = ImageRepo.LEDGrowLights2.alt,
                            attrs = Modifier.classNames("img-fluid", "rounded-4", "shadow", "w-100")
                                .toAttrs { attr("loading", "lazy") }
                        )
                    }
                    Div(
                        attrs = Modifier
                            .classNames("col-lg-6", "order-lg-2", "order-1", "text-lg-start", "text-center")
                            .toAttrs()
                    ) {
                        Div(
                            attrs = Modifier
                                .classNames("d-flex", "flex-column", "align-items-lg-start", "align-items-center", "gap-3")
                                .toAttrs()
                        ) {
                            H2(attrs = LandingSubSectionHeadingStyle.toModifier().toAttrs()) {
                                Text("Do Rebates Apply to Me?")
                            }

                            P(attrs = Modifier.fontSize(1.cssRem).color(Theme.DarkGreen.rgb).classNames("mb-0").toAttrs()) {
                                Text("Are you...")
                            }
                            Ul(
                                attrs = Modifier
                                    .classNames("list-unstyled", "d-flex", "flex-column", "gap-2", "text-start", "w-100", "mb-0")
                                    .toAttrs()
                            ) {
                                listOf(
                                    "...a licensed cultivator?",
                                    "...retrofitting or replacing equipment?",
                                    "...building a new facility or have a new construction?",
                                    "...adding new equipment to your operation?"
                                ).forEach { item ->
                                    Li(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                                        FaCheck(
                                            modifier = LandingCheckmarkStyle.toModifier().classNames("flex-shrink-0"),
                                            size = IconSize.SM
                                        )
                                        Span { Text(item) }
                                    }
                                }
                            }

                            P(attrs = LandingMutedBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                Text("If you answered yes to any of them or are served by a Washington utility, Green Rebates can find rebates for you!")
                            }
                        }
                    }
                }

                Div(attrs = Modifier.classNames("row", "justify-content-center", "mt-5").toAttrs()) {
                    Link(
                        path = "/contact-us?state=Washington",
                        text = "Contact us \u2192",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = LandingCtaSecondaryStyle.toModifier()
                            .width(Width.FitContent)
                            .classNames("mt-1")
                            .role("button")
                            .attrsModifier { attr("aria-label", "Contact us") },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }

            }
        }

        // Section 5: How It Works
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "justify-content-center").toAttrs()) {
                    Div(attrs = Modifier.classNames("col-12", "col-lg-8").toAttrs()) {

                        H2(
                            attrs = LandingSectionHeadingStyle.toModifier()
                                .classNames("mb-2").toAttrs()
                        ) { Text("How to Claim Horticulture Rebates in Washington") }
                        P(
                            attrs = LandingSectionSubtitleStyle.toModifier()
                                .classNames("mb-5").toAttrs()
                        ) { Text("Five steps from first call to incentive check \u2014 we handle the hard parts.") }

                        val steps = listOf(
                            "Tell Us About Your Grow Operation" to
                                "Share your facility size, current equipment, and upcoming upgrade plans. We\'ll map every Washington utility rebate you qualify for before you spend a dollar.",
                            "Equipment Selection" to
                                "We recommend DLC-qualified LEDs, HVAC systems, and other eligible equipment that maximize rebate value while meeting your utility\'s program requirements.",
                            "Utility Applications" to
                                "We submit all pre-approvals, paperwork, and documentation directly to your Washington utility. Nothing gets missed, and nothing falls through the cracks.",
                            "Inspections" to
                                "We may require a pre & post-installation site inspection. We coordinate scheduling and make sure your install passes program specs the first time.",
                            "Collect Your Incentive Check" to
                                "After we\'ve secured every eligible dollar, collect that incentive check and walk away with the maximum return on your upgrade."
                        )
                        steps.forEachIndexed { index, (title, desc) ->
                            Div(attrs = Modifier.classNames("d-flex", "gap-3").toAttrs()) {
                                // Left track: dot, then directed connector trail to next step
                                Div(
                                    attrs = Modifier
                                        .classNames("d-flex", "flex-column", "align-items-center", "flex-shrink-0")
                                        .toAttrs()
                                ) {
                                    // Step node
                                    Div(
                                        attrs = Modifier
                                            .width(13.px).height(13.px)
                                            .borderRadius(50.percent)
                                            .backgroundColor(Theme.DarkGreen.rgb)
                                            .flexShrink(0)
                                            .margin(top = 4.px)
                                            .toAttrs()
                                    ) {}
                                    // Directed connector: line → chevron → line
                                    if (index < steps.lastIndex) {
                                        Div(
                                            attrs = Modifier
                                                .classNames("d-flex", "flex-column", "align-items-center")
                                                .flexGrow(1)
                                                .toAttrs()
                                        ) {
                                            Div(
                                                attrs = Modifier
                                                    .width(2.px)
                                                    .flexGrow(1)
                                                    .backgroundColor(rgba(2, 67, 66, 0.18))
                                                    .margin(top = 4.px)
                                                    .toAttrs()
                                            ) {}
                                            FaChevronDown(
                                                modifier = Modifier
                                                    .color(rgba(2, 67, 66, 0.5))
                                                    .margin(topBottom = 2.px),
                                                size = IconSize.SM
                                            )
                                            Div(
                                                attrs = Modifier
                                                    .width(2.px)
                                                    .flexGrow(1)
                                                    .backgroundColor(rgba(2, 67, 66, 0.18))
                                                    .margin(bottom = 4.px)
                                                    .toAttrs()
                                            ) {}
                                        }
                                    }
                                }
                                // Step content
                                Div(
                                    attrs = Modifier
                                        .classNames(if (index < steps.lastIndex) "pb-4" else "pb-1")
                                        .toAttrs()
                                ) {
                                    H5(attrs = LandingStepCardTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingStepCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }

                        Div(attrs = Modifier.classNames("row", "justify-content-center").toAttrs()) {
                            Link(
                                path = "/contact-us?state=Washington",
                                text = "Get Started \u2192",
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingCtaSecondaryStyle.toModifier()
                                    .width(Width.FitContent)
                                    .role("button")
                                    .attrsModifier { attr("aria-label", "Get Started with Step 1") },
                                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                                openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                            )
                        }

                    }
                }
            }
        }

        // Section 6: WHY GREEN REBATES - SEO
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) { Text("Why Washington Growers Choose Green Rebates") }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        "Washington Utility Expertise" to
                            "We know Washington utility rebate programs inside and out — including every LED horticulture, HVAC, and indoor grow incentive available to Washington cultivation facilities.",
                        "Full-Service for Cannabis & Grow Operations" to
                            "From grow room audits to rebate submissions and checks, we manage every step of the process for cannabis cultivators, greenhouse operators, and other licensed Washington grow operations.",
                        "DLC-Qualified Product Guidance" to
                            "We help you select DLC-qualified LED grow lights and high-efficiency HVAC equipment that maximize your Washington rebate eligibility before you spend a dollar on equipment.",
                        "Stacked Incentive Strategy" to
                            "We identify every opportunity to stack Washington utility rebates with other available energy incentive programs — so your facility captures the maximum possible funding.",
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }
                    }
                }

                P(attrs = LandingMutedWhiteBodyStyle.toModifier().classNames("text-center").toAttrs()) {
                    Text("All of this is powered by our close, trusted relationships with Washington's utility programs.")
                }
            }
        }

        // Section 7: STATS + TESTIMONIALS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            H2(
                attrs = LandingSectionHeadingStyle.toModifier()
                    .classNames("mb-2").toAttrs()
            ) {
                Text("See How We Have Impacted ")
                SpanText(
                    text = "Washington",
                    modifier = Modifier.classNames("fw-italic").color(Theme.MediumGreen.rgb)
                )
            }
            P(
                attrs = LandingSectionSubtitleStyle.toModifier()
                    .classNames("mb-4").toAttrs()
            ) {
                Text("From the Cascades to the Palouse, growers across the Evergreen State are cashing in on energy rebates.")
            }

            Div(attrs = Modifier.classNames("container", "mb-5").toAttrs()) {
                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "g-lg-5", "justify-content-center")
                        .toAttrs()
                ) {
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaDollarSign(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("$55M+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Total Rebates Secured") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaClipboardList(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("400+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Projects Completed") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaBolt(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("420M") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("kWh Saved") }
                        }
                    }
                }
            }

            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "Green Rebates recovered over \$80,000 for our Washington cannabis cultivation facility on LED grow light and HVAC upgrades. They handled every application from start to finish — we just showed up to collect the check." to
                            "Licensed Cannabis Cultivator (Washington)",
                        "I had no idea our Washington utility would pay this much back on LED horticulture lighting. Green Rebates found every dollar available and got us pre-approved before we even purchased equipment. Highly recommend to any Washington grower." to
                            "Greenhouse Operator (Washington)"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1").toAttrs()
                                ) { Text("\u201C") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("- $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        // FAQ
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "py-5").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5").toAttrs()
                ) { Text("FAQs About Energy Rebates in Washington") }

                FaqAccordion(
                    accordionId = "washington-faq",
                    items = listOf(

                        // Utilities section
                        FaqItem(
                            question = "Which Washington utilities work with Green Rebates?",
                            answer = "We work with more than 20 Washington utilities — from large investor-owned utilities like PSE and Avista to municipal and PUD programs across the state. The full list is shown in the utility grid above. If you don't see yours, contact us — we can still help."
                        ),
                        FaqItem(
                            question = "Why do some utilities have an asterisk (*)?",
                            answer = "Utilities marked with an asterisk are ones where Green Rebates has an established track record of securing strong rebates for growers. We've successfully completed multiple projects with these programs and have a working knowledge of their specific requirements — which means a smoother process and more predictable results for your facility."
                        ),

                        // Available Rebates section
                        FaqItem(
                            question = "What types of equipment qualify for Washington rebates?",
                            answer = "Washington utility programs cover a wide range of equipment: DLC-qualified LED grow lights, commercial HVAC systems, dehumidification units, greenhouse automation, irrigation efficiency upgrades, shade and insulation systems, EV chargers, and post-harvest processing equipment. Most energy-intensive upgrades in a licensed cultivation facility have a rebate pathway."
                        ),
                        FaqItem(
                            question = "Do Washington utilities offer rebates for more than just LED grow lights?",
                            answer = "Yes — LED lighting is the most common starting point, but Washington utilities offer incentives across virtually every major energy system in a grow operation. HVAC and dehumidification rebates are often just as large, and some utilities offer custom programs for facilities with high energy loads that go well beyond standard per-fixture rebates."
                        ),

                        // Do Rebates Apply section
                        FaqItem(
                            question = "Who qualifies for Washington grow facility rebates?",
                            answer = "Any commercial grower, licensed cannabis or marijuana cultivator, greenhouse operator, or horticulture facility served by a Washington utility may qualify. Whether you're retrofitting existing equipment, building a new facility, or adding capacity to an existing grow, there's likely a rebate available."
                        ),
                        FaqItem(
                            question = "Can I claim rebates if I'm still planning my build or upgrade?",
                            answer = "Yes — and that's actually the best time to get started. Most Washington utility rebate programs require pre-approval before equipment is purchased or installed. Contacting Green Rebates before you buy ensures you don't accidentally disqualify yourself from funding."
                        ),

                        // How It Works section
                        FaqItem(
                            question = "When can I expect my incentive or rebate check?",
                            answer = "The timeline typically follows five steps: (1) we assess your facility and map available rebates; (2) you select DLC-qualified equipment with our guidance; (3) we submit all pre-approval paperwork to your utility; (4) we coordinate any required pre- and post-installation inspections; and (5) once everything is verified, your incentive check is issued by the utility. From first call to check, most projects are completed in 60–120 days depending on the utility's processing timeline."
                        ),

                        // Why Green Rebates section
                        FaqItem(
                            question = "Why work with Green Rebates instead of applying directly?",
                            answer = "Washington utility rebate programs have specific pre-approval windows, product eligibility requirements, and documentation standards that are easy to miss if you're doing this for the first time. Green Rebates knows every utility's process, identifies stacking opportunities across multiple programs, and handles all the paperwork — so you capture the maximum possible funding without the administrative headache."
                        ),
                        FaqItem(
                            question = "Is cannabis cultivation in Washington eligible for energy rebates?",
                            answer = "Yes. Washington was one of the first states to legalize recreational cannabis, and licensed cannabis and marijuana grow facilities qualify for commercial utility rebate programs just like any other horticulture operation. Green Rebates has completed projects with Washington cannabis cultivators across multiple utility territories."
                        ),

                        // General / bottom
                        FaqItem(
                            question = "How much can a Washington grow facility get back in rebates?",
                            answer = "It depends on your facility size, equipment type, and which utility serves your location. Washington cannabis and horticulture growers have received anywhere from tens of thousands to over \$100,000 back on a single project through Green Rebates. The best way to get a real number is to contact us for a free rebate estimate."
                        )
                    )
                )
            }
        }

        RelatedSection(listOf(
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/article/top-10-high-efficiency-led-rebates-for-commercial-cultivators-in-2026" to "Top 10 High-Efficiency LED Rebates for Commercial Cultivators in 2026",
            "/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives" to "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingCtaHeadingStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) { Text("Ready to Claim Your Washington Grow Rebates?") }
                P(
                    attrs = LandingCtaBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto").toAttrs()
                ) {
                    Text("Don't let your Washington cultivation facility miss out on LED grow light rebates, HVAC incentives, and horticulture energy savings programs. Green Rebates handles everything — from pre-approval to check.")
                }
                Link(
                    path = "/contact-us?state=Washington",
                    text = "Get a Free Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free Washington LED grow light and HVAC rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

    }
}


// UNUSED COMPONENT - NOT USED IN THIS FRAMEWORK FOR STATE PAGES
// WHY NOW — urgency callout (new section, not on MI/MN)
//Section(
//attrs = Modifier
//.classNames("container-fluid", "px-0", "py-5", "py-lg-7")
//.backgroundColor(Theme.DarkGreen.rgb)
//.toAttrs()
//)
//{
//    Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
//        H2(
//            attrs = LandingDarkSectionHeadingStyle.toModifier()
//                .classNames("mb-3").toAttrs()
//        ) { Text("Washington Growers Are Leaving Money on the Table") }
//        P(
//            attrs = LandingDarkBodyStyle.toModifier()
//                .classNames("mb-4", "mx-auto").toAttrs()
//        ) {
//            Text("Energy is the single largest operating cost for any indoor cannabis or horticulture facility in Eastern Washington — and utility rebate budgets don't carry over year to year. Washington was among the first states to legalize cannabis, and experienced growers here know that missing the pre-approval window means permanently losing that funding. Green Rebates keeps you ahead of the deadline.")
//        }
//        Link(
//            path = "/contact-us?state=Washington",
//            text = "Check Your Washington Rebates Now \u2192",
//            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
//            modifier = LandingCtaOutlineStyle.toModifier()
//                .role("button")
//                .attrsModifier { attr("aria-label", "Check your Washington grow light and HVAC rebates now") },
//            openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
//            openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
//        )
//    }
//}